import React, { useState } from "react";
import ModalWindow from "modules/common/modalWindow/ModalWindow";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { FormControl } from "@remo-co/ui-core/src/components/FormControl";
import { InputLabel } from "@remo-co/ui-core/src/components/InputLabel";
import { Popover } from "@remo-co/ui-core/src/components/Popover";
import { TextField } from "@remo-co/ui-core/src/components/TextField";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ScrollableView } from "@remo-co/ui-core/src/components/ScrollableView";
import { Select } from "@remo-co/ui-core/src/components/Select";
import { Box } from "@remo-co/ui-core/src/components/Box";
import "./AddCompanyMember.scss";
import { ExpandMore } from "@remo-co/ui-core/src/icons/ExpandMore";
import { isValidEmailAddress } from "helpers/emailHelper";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useI18n } from "i18n";
import { IEvent } from "modules/event/types";
import { ICompany } from "modules/company/types";
import { styles } from "./AddCompanyMemberStyles";
import { addMemberReq } from "./apis";
import CheckBoxList from "./CheckBoxList";

interface IAddMemberParam {
  company: ICompany;
  allEvents: IEvent[];
  handleClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateMembers: (members: any[]) => void;
}

const ROLE_INFO: { [key: string]: { [key: string]: string } } = {
  manager: {
    model: "manager",
    view: "Event Manager",
  },
  assistant: {
    model: "assistant",
    view: "Event Assistant",
  },
};

const AddCompanyMember = ({
  company,
  allEvents,
  handleClose,
  updateMembers,
}: IAddMemberParam): JSX.Element => {
  const [email, setEmail] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const [events, setEvents] = useState<string[]>([]);
  const [showList, setShowList] = useState<boolean>(false);
  const [isInputValid, setIsInputValid] = useState<boolean>(false);
  const { t } = useI18n(["common", "settings"]);

  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();

  React.useEffect(() => {
    setIsInputValid(!!(email && role));
  }, [email, role]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    setEmail(input);
  };

  const handleSelectRole = (selected: string) => {
    setRole(selected);
  };

  const handleEventClick = (event: React.MouseEvent) => {
    if (allEvents?.length > 0) {
      setAnchorEl(event.currentTarget);
      setShowList(!showList);
    }
  };

  // eslint-disable-next-line consistent-return
  const addMember = async (event?: React.FormEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!isValidEmailAddress(email)) {
      return addErrorNotification({
        message: t("common:invalid.email.message"),
      });
    }

    const res = await addMemberReq(company.id as string, {
      email,
      role,
      events,
    });

    if (res.isSuccess) {
      addSuccessNotification({
        message: t("settings:member.invite.success"),
      });
      updateMembers(res.members);
      handleClose();

      // eslint-disable-next-line consistent-return
      return;
    }

    addErrorNotification({
      message: res.error ?? t("settings:member.add.error"),
    });
  };

  return (
    <div className="modalView" data-testid="add-company-member-dialog">
      <ModalWindow
        onClose={handleClose}
        disableCloseOnBackdrop
        fullScreen={false}
        className="add-member-ctn"
        sx={styles.container}
        title={t("settings:team.member.add")}
        open
      >
        <div>
          <Typography variant="body1" sx={styles.subtitle}>
            {t("settings:team.member.add.desc")}
          </Typography>
        </div>
        <ScrollableView withAppBar={false}>
          <form onSubmit={addMember} noValidate>
            <Box sx={styles.formCtn}>
              <div className="inputs">
                <Box sx={styles.input}>
                  <InputLabel sx={styles.label}>{t("common:email")}</InputLabel>
                  <FormControl>
                    <TextField
                      id="memberEmail"
                      name="memberEmail"
                      value={email}
                      variant="outlined"
                      sx={styles.text}
                      onChange={handleEmailChange}
                    />
                  </FormControl>
                </Box>

                <Box sx={styles.input}>
                  <InputLabel sx={styles.label}>{t("common:role")}</InputLabel>
                  <FormControl>
                    <Select
                      multiple={false}
                      value={role}
                      onChange={handleSelectRole}
                      placeholder={
                        (ROLE_INFO[role] && ROLE_INFO[role].view) ||
                        "Select Access Level"
                      }
                      options={[
                        {
                          value: ROLE_INFO.manager.model,
                          label: `${t("settings:event.manager")} (${t(
                            "settings:event.manager.desc",
                          )})`,
                        },
                        {
                          value: ROLE_INFO.assistant.model,
                          label: `${t("settings:event.assistant")} (${t(
                            "settings:event.assistant.desc",
                          )})`,
                        },
                      ]}
                      sx={styles.border}
                    />
                  </FormControl>
                </Box>

                {role === ROLE_INFO.assistant.model && (
                  <div className="input">
                    <InputLabel sx={styles.label}>
                      {t("settings:event.permission")}
                    </InputLabel>
                    <FormControl>
                      <Box sx={styles.buttonContainer}>
                        <Button
                          onClick={handleEventClick}
                          sx={styles.dropdownButton}
                        >
                          {!allEvents || allEvents.length === 0 ? (
                            <Typography sx={styles.buttontext}>
                              {t("settings:no.events")}
                            </Typography>
                          ) : (
                            <>
                              <Typography
                                variant="body1"
                                sx={styles.placeholderText}
                              >
                                {events.length
                                  ? `${events.length} Events`
                                  : t("settings:select.events")}
                              </Typography>
                              <ExpandMore sx={styles.chevron} />
                            </>
                          )}
                        </Button>
                      </Box>
                    </FormControl>
                    <Popover
                      open={showList}
                      onClose={() => setShowList(false)}
                      anchorEl={anchorEl}
                      anchorOrigin={{
                        vertical: 180,
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      sx={styles.popover}
                    >
                      <CheckBoxList
                        allEvents={allEvents}
                        mappedEvents={events}
                        onChange={(updatedEvents) => {
                          setEvents(updatedEvents);
                        }}
                      />
                    </Popover>
                  </div>
                )}
              </div>
              <Box sx={styles.action}>
                <Button
                  type="submit"
                  variant="secondary"
                  color="blue"
                  disabled={!isInputValid}
                  sx={styles.submit}
                >
                  {t("settings:member.add")}
                </Button>
                <Button
                  variant="tertiary"
                  color="gray"
                  sx={styles.cancel}
                  onClick={handleClose}
                >
                  {t("common:button.cancel")}
                </Button>
              </Box>
            </Box>
          </form>
        </ScrollableView>
      </ModalWindow>
    </div>
  );
};

export default AddCompanyMember;
